import OAuthSimple from 'oauthsimple';
import queryString from 'query-string';

import { ApiGetAuthToken, ApiGetRequestToken, ApiSetUserTokens } from '../actions/Api';
import { openModal } from '../actions/Modal';
import { ApiActions } from '../constants/ApiActions';
import * as constants from '../constants/Twitter';
import {
  getHost,
  getPort,
  getTwitterAppKey,
  getTwitterAppSecret,
} from '../lib/configuration/TunerVarUtils';
import { tryPromise } from '../lib/CoreUtils';
import ApplicationError from '../lib/error/ApplicationError';

/**
 * Utility function for retrieving an object with
 * the app keys for communicating with twitter
 *
 * @param {object} state - redux state of the app
 * @returns {object} app token and secret
 * @private
 */
function getOauthData(state) {
  return {
    authKey: getTwitterAppKey(state),
    authSecret: getTwitterAppSecret(state),
  };
}

/**
 * Produces necessary query parameters for a signed OAuth1.0 request,
 * given our app token and secrets, which we need to include
 * in any request to twitter.
 *
 * @param {string} authKey - twitter oauth application key
 * @param {string} authSecret - twitter oauth application secret
 * @returns {OAuthSimple}
 * @private
 */
function buildOAuthInterface(authKey, authSecret) {
  const oauth = new OAuthSimple(authKey, authSecret);
  oauth.setAction('POST');
  return oauth;
}

/**
 * Action creator for setting a listeners twitter oauth credentials.
 *
 * @param  {object} options - list of options passed into content
 * @param  {string} options.userToken - userToken for twitter oauth
 * @param  {string} options.userTokenSecret - userTokenSecret for twitter oauth
 * @param {string} options.screenName - screenName of user on twitter
 * @param options.token
 * @param options.secret
 * @param options.twitterHandle
 * @returns {Function} - a Thunk function dispatching the SetUserTokens
 */
export function setUserTokens({ token, secret, twitterHandle }) {
  return dispatch => {
    return dispatch(ApiSetUserTokens({ token, secret, twitterHandle }));
  };
}

/**
 * The final step in connecting to twitter - receiving the response to our request
 * for authentication token and secret corresponding to our listener
 *
 * @param {string} responseText - response of request to twitter/oauth/auth_token
 * @param {Function} dispatch - Redux dispatch method
 * @private
 */
function handleAuthTokenResponse(responseText, dispatch) {
  if (responseText) {
    const queryParams = queryString.parse(responseText);
    const token = queryParams.oauth_token;
    const secret = queryParams.oauth_token_secret;
    const twitterHandle = queryParams.screen_name;

    if (!token || !secret) {
      throw new ApplicationError(constants.TwitterErrors.AUTH_DENIED);
    } else {
      setUserTokens({ token, secret, twitterHandle })(dispatch);
    }
  } else {
    throw new ApplicationError(constants.TwitterErrors.UNABLE_TO_FETCH_ACCESS_TOKEN);
  }
}

/**
 * The second to last step in connecting to twitter - requesting a token + secret
 * given the (request)token + verifier we received from ApiGetRequestToken
 *
 * @param {string} token - twitter oauth token
 * @param {string} verifier - twitter oauth verifier
 * @returns {Promise} * ( technically returns a dispatchable action which returns a promise )
 */
export function fetchAuthToken(token, verifier) {
  return (dispatch, getState) => {
    return tryPromise(() => {
      // remove the callback method on the window object
      window.onOAuthCallBack = null;

      if (!token) {
        throw new ApplicationError(constants.TwitterErrors.MISSING_REQUEST_TOKEN);
      } else if (!verifier) {
        throw new ApplicationError(constants.TwitterErrors.MISSING_VERIFIER);
      }

      const { authKey, authSecret } = getOauthData(getState());
      const oauth = buildOAuthInterface(authKey, authSecret);

      const accessTokenRequestPayload = oauth.sign({
        path: constants.ACCESS_TOKEN_URL,
        parameters: {
          oauth_token: token,
        },
        signatures: {
          consumer_key: authKey,
          shared_secret: authSecret,
        },
      });

      const queryParams = accessTokenRequestPayload.parameters;
      const formData = queryString.stringify({ oauth_verifier: verifier });
      return dispatch(ApiGetAuthToken({ queryParams, formData })).then(response => {
        handleAuthTokenResponse(response, dispatch);
      });
    }).catch(e => {
      if (e instanceof ApplicationError) {
        switch (e.message) {
          case constants.TwitterErrors.MISSING_REQUEST_TOKEN:
          case constants.TwitterErrors.MISSING_VERIFIER:
          case constants.TwitterErrors.UNABLE_TO_FETCH_ACCESS_TOKEN:
          case constants.TwitterErrors.AUTH_DENIED:
            dispatch(
              openModal({
                modalType: 'ModalCoachMark',
                title: '',
                body: e.message,
                buttons: [],
              }),
            );
            return dispatch({
              type: ApiActions.GET_AUTH_TOKEN_FAIL,
              data: {
                msg: 'Error: ' + e.data,
              },
            });
          default:
            throw e;
        }
      } else {
        throw e;
      }
    });
  };
}

/**
 * @param {object} twitterOauthWindow - the window object into which to inject the twitter oauth url
 * @returns {Function} returns a function that will inject the oauth token into the twitterOauthWindow
 * @private
 */
function handleRequestTokenResponse(twitterOauthWindow) {
  return function (responseText) {
    if (!responseText || !responseText.includes('oauth_token')) {
      throw new ApplicationError(constants.TwitterErrors.EMPTY_REQUEST_TOKEN_RESPONSE);
    }

    // Twitter will prompt the listener to authorize our app.  After the connection
    // is made or denied, twitter will call back to '/settings/twitterCallback' in the same window,
    // which calls window.onOauthCallback on the window.opener ( the instance of our app which called this
    // method ), on a successful connection to twitter fetchAuthToken() will continue the
    // process of getting an auth token we can use to post on the listeners behalf
    if (twitterOauthWindow === null) {
      throw new ApplicationError(constants.TwitterErrors.POPUP_BLOCKED);
    } else {
      twitterOauthWindow.location.href = constants.AUTHORIZE_URL + '?' + responseText;
      twitterOauthWindow.focus();
    }
  };
}

/**
 * The first action that will kick off the twitter OAuth connect flow
 * Opens a new window and calls the API method ApiGetRequestToken, populating the
 * URL of the opened window with the response of the API Request.
 * The request is proxied through our backend, to the twitter REQUEST_TOKEN_URL
 *
 * We also define a method on the window object, for the pop-up to call back into
 * once we have received the callback from twitter.  This will be removed
 * by the method it calls, fetchAuthToken, or upon catching an error
 *
 * @returns {Promise} * ( technically returns a dispatchable action which returns a promise )
 */
export function connectToTwitter() {
  return (dispatch, getState) => {
    const state = getState();
    const { authKey, authSecret } = getOauthData(state);
    const OAuth = buildOAuthInterface(authKey, authSecret);
    const host = getHost(state);
    const port = getPort(state);

    const payload = OAuth.sign({
      path: constants.REQUEST_TOKEN_URL,
      parameters: {
        oauth_callback: 'https://' + host + ':' + port + '/twitter/oauthAuthorizationCallback.jsp',
      },
      signatures: {
        consumer_key: authKey,
        shared_secret: authSecret,
      },
    });

    /**
     * Method called by the twitterOauthWindow ( via window.opener ) upon
     * successful callback by twitter
     *
     * @param {string} oauth_token - twitter oauth token
     * @param {string} oauth_verifier - twitter oauth verifier
     */
    window.onOAuthCallBack = function (oauth_token, oauth_verifier) {
      dispatch(fetchAuthToken(oauth_token, oauth_verifier));
    };

    const twitterOauthWindow = window.open('about:blank', 'oauth');

    // Axios, for some reason, 'un-encodes' certain characters, such as ':', when building
    // a query string from the parameters parameter, so we build the url ourselves
    const query = payload.signed_url.substring(payload.signed_url.indexOf('?'));

    return dispatch(ApiGetRequestToken(query))
      .then(handleRequestTokenResponse(twitterOauthWindow))
      .catch(e => {
        if (e instanceof ApplicationError) {
          twitterOauthWindow.close();
          // remove the callback method on the window object
          window.onOAuthCallBack = null;
          switch (e.message) {
            case constants.TwitterErrors.EMPTY_REQUEST_TOKEN_RESPONSE:
            case constants.TwitterErrors.POPUP_BLOCKED:
              dispatch(
                openModal({
                  modalType: 'ModalCoachMark',
                  title: '',
                  body: e.message,
                  buttons: [],
                }),
              );
              return dispatch({
                type: ApiActions.GET_REQUEST_TOKEN_FAIL,
                data: {
                  msg: 'Error: ' + e.data,
                },
              });
            default:
              throw e;
          }
        } else {
          throw e;
        }
      });
  };
}
